// Graph view — sigma renderer + graphology data model + ForceAtlas2 layout.
//
// Reads /renketsu/stats (header strip) and /renketsu/graph (full node+edge
// payload, limit 5000). Click a node → right glass panel slides in with the
// node's payload + in/out edges.
//
// Coloring: single subdued tone for now. Live SSE updates come in a later
// phase; refresh is manual via the stats header for now.

import { useQuery } from "@tanstack/react-query";
import { ArrowDownRight, ArrowUpRight, Maximize2, Minus, Plus, X } from "lucide-react";
import Graph from "graphology";
import { useEffect, useMemo, useRef, useState } from "react";
import Sigma from "sigma";
import { EdgeLineProgram } from "sigma/rendering";
import { useParams, useSearchParams } from "react-router";

import { colorForNodeType } from "@/components/canvas/colors";
import { assignCommunityLayout } from "@/components/canvas/community-layout";
import { communityHue, convexHull, padHull } from "@/components/canvas/hull";
import {
	drawSourceBadge,
	normalizeSourceGlyphKey,
	SOURCE_GLYPH,
	SourceBrandIcon,
	sourceGlyphFor,
} from "@/components/canvas/source-glyph";
import { GlassPanel } from "@/components/glass";

interface StatsResponse {
	node_count: number;
	edge_count: number;
	vector_count: number;
	vector_dim: number;
	vector_tier: "flat" | "diskann";
	schema_version: number;
	diskann_generation: number;
}

interface GraphNode {
	id: string;
	type: string;
	source: string;
	external_id: string;
	payload: unknown;
}

interface GraphEdge {
	id: string;
	from: string;
	to: string;
	relation: string;
	weight: number;
	payload: unknown;
}

interface GraphResponse {
	nodes: GraphNode[];
	edges: GraphEdge[];
}

const NODE_FILL = "#d4d4d8";
// Sky-300, picked off the lighter end of the brand asterisk gradient
// so the active state reads as on-brand instead of generic "white".
const NODE_FILL_HOVER = "#7dd3fc";
// Smaller base so leaf nodes read as small dots. Size scales with
// degree below (more-connected nodes get bigger), giving the graph a
// visual hierarchy: hubs pop, leaves recede. Kept small on purpose —
// fatter nodes overlap on dense graphs and paint over the gaps between
// communities, which reads as one undifferentiated blob.
const NODE_SIZE_BASE = 3;
const NODE_DEGREE_SCALE = 0.4;
const NODE_DEGREE_CAP = 12;
const NODE_SIZE_HOVER_DELTA = 3;
function sizeForDegree(degree: number): number {
	return NODE_SIZE_BASE + Math.min(degree, NODE_DEGREE_CAP) * NODE_DEGREE_SCALE;
}
// Hairline edges. WebGL lines look harsh at full opacity; pulling the alpha
// way down + size to 0.2 gives the wireframe / stroke feel.
const EDGE_FILL = "rgba(255,255,255,0.22)";
// Slightly tinted toward the hover color so incident edges feel linked
// to the highlighted node rather than just "brighter."
const EDGE_FILL_HOVER = "rgba(125,211,252,0.55)";

export default function GraphView() {
	const { id: projectId } = useParams<{ id: string }>();
	const [searchParams, setSearchParams] = useSearchParams();
	const containerRef = useRef<HTMLDivElement>(null);
	const sigmaRef = useRef<Sigma | null>(null);
	const [selected, setSelected] = useState<GraphNode | null>(null);
	// Mirror of `selected` for sigma's imperative event handlers (which
	// capture state in their closure on first bind and don't re-render
	// when React state changes). The ref always points at the current
	// selection so leaveNode etc. read the right value.
	const selectedRef = useRef<GraphNode | null>(null);
	useEffect(() => {
		selectedRef.current = selected;
	}, [selected]);

	// Whenever the selection changes, paint a persistent highlight on
	// the selected node + its incident edges. Clear any prior highlight
	// first so swapping selections doesn't leave a stale halo behind.
	useEffect(() => {
		const renderer = sigmaRef.current;
		if (!renderer) return;
		const graph = renderer.getGraph();

		// Reset every node + edge to default. Restore each node to its
		// degree-scaled `_baseSize` and its type/community `_baseColor`
		// (set at build time) — resetting to a flat fill here is what made
		// clicking a node wipe every other node's colour. This is O(N+E)
		// but our graphs are bounded so the cost is trivial.
		graph.forEachNode((n) => {
			const baseSize = (graph.getNodeAttribute(n, "_baseSize") as number) ?? NODE_SIZE_BASE;
			const baseColor =
				(graph.getNodeAttribute(n, "_baseColor") as string | undefined) ??
				NODE_FILL;
			graph.setNodeAttribute(n, "size", baseSize);
			graph.setNodeAttribute(n, "color", baseColor);
		});
		graph.forEachEdge((e) => {
			graph.setEdgeAttribute(e, "color", EDGE_FILL);
		});

		// Then paint the selection highlight: bump the selected node by
		// a fixed delta on top of its base size, change color to the
		// brand sky tint, brighten incident edges.
		if (selected && graph.hasNode(selected.id)) {
			const baseSize = (graph.getNodeAttribute(selected.id, "_baseSize") as number) ?? NODE_SIZE_BASE;
			graph.setNodeAttribute(selected.id, "size", baseSize + NODE_SIZE_HOVER_DELTA);
			graph.setNodeAttribute(selected.id, "color", NODE_FILL_HOVER);
			for (const e of graph.edges(selected.id)) {
				graph.setEdgeAttribute(e, "color", EDGE_FILL_HOVER);
			}
		}
		renderer.refresh();
	}, [selected]);

	// Helper: re-select by id and pan + zoom the camera to the node so the
	// user can follow chains of edges with a clear visual cue. ratio:0.2
	// is a 5x zoom-in; small enough to feel "we moved into this node",
	// not so small that one node fills the whole canvas.
	const navigateToNode = (nodeId: string, nodes: GraphNode[]) => {
		const next = nodes.find((n) => n.id === nodeId);
		if (!next) return;
		setSelected(next);
		const renderer = sigmaRef.current;
		if (!renderer || !renderer.getGraph().hasNode(nodeId)) return;
		const display = renderer.getNodeDisplayData(nodeId);
		if (!display) return;
		renderer.getCamera().animate(
			{ x: display.x, y: display.y, ratio: 0.2 },
			{ duration: 600 },
		);
	};

	const statsQ = useQuery({
		queryKey: ["renketsu", "stats", projectId],
		queryFn: async () => {
			const r = await fetch(`/api/projects/${projectId}/renketsu/stats`, {
				credentials: "include",
			});
			if (!r.ok) throw new Error(`stats: ${r.status}`);
			return (await r.json()) as StatsResponse;
		},
		enabled: !!projectId,
		refetchInterval: 5000,
	});

	const graphQ = useQuery({
		queryKey: ["renketsu", "graph", projectId],
		queryFn: async () => {
			const r = await fetch(
				`/api/projects/${projectId}/renketsu/graph?limit=5000`,
				{ credentials: "include" },
			);
			if (!r.ok) throw new Error(`graph: ${r.status}`);
			return (await r.json()) as GraphResponse;
		},
		enabled: !!projectId,
		// Refresh when the cached stats counts change (rough live signal).
		refetchInterval: 10000,
	});
	const presentSources = useMemo(() => {
		const seen = new Set<string>();
		for (const node of graphQ.data?.nodes ?? []) {
			const key = normalizeSourceGlyphKey(node.source);
			if (key && SOURCE_GLYPH[key]) seen.add(key);
		}
		return [...seen];
	}, [graphQ.data?.nodes]);

	// Build / rebuild the sigma instance whenever the graph payload changes.
	useEffect(() => {
		if (!containerRef.current || !graphQ.data) return;

		const g = new Graph({ multi: true, type: "directed" });
		const nodes = graphQ.data.nodes;
		const edges = graphQ.data.edges;

		// Seed positions on a unit circle so ForceAtlas2 has something to
		// expand from. Random seeding can collapse near-identical points.
		// Initial size is the base; we resize per-degree after edges land.
		nodes.forEach((n, i) => {
			const angle = (2 * Math.PI * i) / Math.max(nodes.length, 1);
			// Spec 37: `community` nodes are synthetic Louvain aggregates,
			// not real content. They anchor the layout (members cluster
			// around their community) but are NOT drawn — a corpus can have
			// hundreds of them, and drawing them buries the real graph. Kept
			// in the graphology model (hidden) so the layout can use them.
			const isCommunity = n.type === "community";
			g.addNode(n.id, {
				x: Math.cos(angle),
				y: Math.sin(angle),
				size: NODE_SIZE_BASE,
				// Spec 34: colour by node type to match /welcome's
				// visual vocabulary. The previous flat #d4d4d8 fill
				// dates from before the welcome canvas existed.
				color: colorForNodeType(n.type),
				label: shortLabel(n),
				// `_type` (not `type` — that's reserved by sigma to select a
				// node program) is what the community layout keys off to find
				// the `community` anchor nodes.
				_type: n.type,
				_node: n,
				_baseSize: NODE_SIZE_BASE,
				_baseColor: colorForNodeType(n.type),
				hidden: isCommunity,
			});
		});
		edges.forEach((e) => {
			if (!g.hasNode(e.from) || !g.hasNode(e.to)) return;
			g.addEdge(e.from, e.to, {
				size: 0.2,
				color: EDGE_FILL,
				label: e.relation,
				// `relation` (not just the display `label`) is what the
				// community layout keys off; without it the layout silently
				// fell back to a flat pass. `member_of` edges only connect to
				// the hidden community nodes, so don't draw them.
				relation: e.relation,
				_edge: e,
				hidden: e.relation === "member_of",
			});
		});

		// Second pass: scale each node by its degree so hubs read bigger
		// and leaves stay as small dots. Stored on `_baseSize` so hover/
		// select handlers can restore it on leave/deselect without
		// recomputing.
		g.forEachNode((id) => {
			const baseSize = sizeForDegree(g.degree(id));
			g.setNodeAttribute(id, "size", baseSize);
			g.setNodeAttribute(id, "_baseSize", baseSize);
		});

		// Spec 37: community-aware two-level layout. Places members around
		// their community centroid and communities around each other, so the
		// graph reads as separated clusters instead of one ball. Falls back
		// to plain ForceAtlas2 (LinLog) when there are < 2 communities.
		assignCommunityLayout(g);

		// Tear down any previous renderer before mounting a new one.
		if (sigmaRef.current) {
			sigmaRef.current.kill();
			sigmaRef.current = null;
		}

		const renderer = new Sigma(g, containerRef.current, {
			renderLabels: true,
			renderEdgeLabels: true,
			// Default is EdgeRectangleProgram — heavy, solid-bar look at any
			// non-trivial size. Swap to EdgeLineProgram for the thin
			// antialiased stroke we want.
			edgeProgramClasses: { line: EdgeLineProgram },
			defaultEdgeType: "line",
			defaultNodeColor: NODE_FILL,
			defaultEdgeColor: EDGE_FILL,
			labelColor: { color: "rgba(255,255,255,0.78)" },
			labelSize: 11,
			labelWeight: "450",
			labelFont: "Inter, system-ui, sans-serif",
			edgeLabelColor: { color: "rgba(255,255,255,0.4)" },
			edgeLabelSize: 9,
			edgeLabelFont: "Inter, system-ui, sans-serif",
			// Tighter label decimation: at zoom-out only a few representative
			// labels show; zoom-in unlocks the rest. labelDensity:2 + a
			// permissive size threshold was producing mass overlap.
			labelDensity: 0.7,
			// Tighter label decimation: at any given zoom level, fewer
			// labels render so the dense clusters from the earlier
			// screenshot don't overlap into mush. Both knobs scale
			// inversely with density.
			labelGridCellSize: 280,
			labelRenderedSizeThreshold: 9,
			// Disable sigma's default hover label box (opaque white rect).
			// Color-only highlight from our enter/leave handlers reads
			// cleanly against the dark canvas without it.
			defaultDrawNodeHover: () => {},
			// Wide zoom range so dense graphs are explorable + mouse-
			// wheel ticks are gentler. Default sigma zoomingRatio is
			// 1.7 (each tick = 70% closer/farther); 1.4 feels smooth.
			minCameraRatio: 0.02,
			maxCameraRatio: 50,
			zoomingRatio: 1.4,
		});

		// Hover: pump the active node (base + a fixed delta, color the
		// brand sky tint) and brighten its incident edges. Leave the
		// rest of the graph alone so the user's mental map of the
		// cluster doesn't visibly shift on every move.
		renderer.on("enterNode", ({ node }) => {
			const graph = renderer.getGraph();
			const baseSize = (graph.getNodeAttribute(node, "_baseSize") as number) ?? NODE_SIZE_BASE;
			graph.setNodeAttribute(node, "size", baseSize + NODE_SIZE_HOVER_DELTA);
			graph.setNodeAttribute(node, "color", NODE_FILL_HOVER);
			for (const e of graph.edges(node)) {
				graph.setEdgeAttribute(e, "color", EDGE_FILL_HOVER);
			}
			if (containerRef.current) containerRef.current.style.cursor = "pointer";
			renderer.refresh();
		});
		renderer.on("leaveNode", ({ node }) => {
			const graph = renderer.getGraph();
			const selectedId = selectedRef.current?.id;
			const baseSize = (graph.getNodeAttribute(node, "_baseSize") as number) ?? NODE_SIZE_BASE;
			// Restore the type-coloured base; falls back to NODE_FILL
			// for nodes added before _baseColor was stored.
			const baseColor =
				(graph.getNodeAttribute(node, "_baseColor") as string | undefined) ??
				NODE_FILL;
			// If the leaving node is the active selection, keep its
			// highlight intact; otherwise reset to its degree-scaled
			// base size + type colour.
			if (node === selectedId) {
				graph.setNodeAttribute(node, "size", baseSize + NODE_SIZE_HOVER_DELTA);
				graph.setNodeAttribute(node, "color", NODE_FILL_HOVER);
			} else {
				graph.setNodeAttribute(node, "size", baseSize);
				graph.setNodeAttribute(node, "color", baseColor);
			}
			// Reset incident edges, but preserve highlight on edges that
			// are also incident to the selection so the selected
			// neighborhood remains visually wired.
			for (const e of graph.edges(node)) {
				const incidentToSelected =
					!!selectedId &&
					(graph.source(e) === selectedId || graph.target(e) === selectedId);
				graph.setEdgeAttribute(
					e,
					"color",
					incidentToSelected ? EDGE_FILL_HOVER : EDGE_FILL,
				);
			}
			if (containerRef.current) containerRef.current.style.cursor = "";
			renderer.refresh();
		});
		renderer.on("clickNode", ({ node }) => {
			const attrs = renderer.getGraph().getNodeAttributes(node) as { _node: GraphNode };
			setSelected(attrs._node);
		});
		// Labels render on a canvas overlay outside sigma's WebGL hit-test,
		// so clicking a label falls through here. Find the closest node to
		// the cursor; if it's within label-grab range, treat as a node
		// click. Outside that range = a real "click empty space".
		renderer.on("clickStage", (payload) => {
			const evt = (payload as { event: { x: number; y: number } }).event;
			if (!evt || typeof evt.x !== "number") {
				setSelected(null);
				return;
			}
			const graph = renderer.getGraph();
			let closestId: string | null = null;
			let closestDist = Infinity;
			graph.forEachNode((nodeId) => {
				// Don't let a hidden community centroid grab the click.
				if (graph.getNodeAttribute(nodeId, "hidden")) return;
				const display = renderer.getNodeDisplayData(nodeId);
				if (!display) return;
				const vp = renderer.framedGraphToViewport(display);
				const d = Math.hypot(vp.x - evt.x, vp.y - evt.y);
				if (d < closestDist) {
					closestDist = d;
					closestId = nodeId;
				}
			});
			// 90px covers the node circle (~5px) plus its label width plus
			// padding. Tuned for our 11px Inter labels; widen if labels
			// ever grow.
			if (closestId && closestDist < 90) {
				const attrs = graph.getNodeAttributes(closestId) as { _node: GraphNode };
				setSelected(attrs._node);
			} else {
				setSelected(null);
			}
		});

		sigmaRef.current = renderer;

		// Two overlay canvases. The hull canvas goes UNDERNEATH sigma's
		// layers (inserted as the container's first child) so the community
		// territories sit behind the nodes — drawing them on top fogged
		// every node at zoom. The badge canvas stays on top so source
		// glyphs annotate the nodes.
		const mkCanvas = () => {
			const c = document.createElement("canvas");
			c.style.position = "absolute";
			c.style.inset = "0";
			c.style.width = "100%";
			c.style.height = "100%";
			c.style.pointerEvents = "none";
			return c;
		};
		const hullCanvas = mkCanvas();
		const overlay = mkCanvas();
		if (containerRef.current) {
			containerRef.current.insertBefore(hullCanvas, containerRef.current.firstChild);
			containerRef.current.appendChild(overlay);
		}

		const fit = (canvas: HTMLCanvasElement, el: HTMLElement) => {
			const ctx = canvas.getContext("2d");
			if (!ctx) return null;
			const dpr = window.devicePixelRatio || 1;
			const cw = el.clientWidth;
			const ch = el.clientHeight;
			if (canvas.width !== cw * dpr || canvas.height !== ch * dpr) {
				canvas.width = cw * dpr;
				canvas.height = ch * dpr;
			}
			ctx.setTransform(dpr, 0, 0, dpr, 0, 0);
			ctx.clearRect(0, 0, cw, ch);
			return ctx;
		};

		const draw = () => {
			const el = containerRef.current;
			if (!el) return;
			const hullCtx = fit(hullCanvas, el);
			const badgeCtx = fit(overlay, el);
			if (!hullCtx || !badgeCtx) return;

			// Spec 37: faint community territories, on the BEHIND canvas.
			const byCommunity = new Map<string, { x: number; y: number }[]>();
			g.forEachNode((id, attrs) => {
				if (attrs.hidden) return;
				const community = attrs._community as string | null | undefined;
				if (!community) return;
				const vp = renderer.graphToViewport({ x: attrs.x, y: attrs.y });
				const pts = byCommunity.get(community);
				if (pts) pts.push(vp);
				else byCommunity.set(community, [vp]);
			});
			for (const [community, pts] of byCommunity) {
				if (pts.length < 3) continue;
				const hull = padHull(convexHull(pts), 16);
				if (hull.length < 3) continue;
				const hue = communityHue(community);
				hullCtx.beginPath();
				hull.forEach((p, i) =>
					i === 0 ? hullCtx.moveTo(p.x, p.y) : hullCtx.lineTo(p.x, p.y),
				);
				hullCtx.closePath();
				hullCtx.fillStyle = `hsla(${hue}, 70%, 60%, 0.12)`;
				hullCtx.fill();
				hullCtx.strokeStyle = `hsla(${hue}, 70%, 65%, 0.32)`;
				hullCtx.lineWidth = 1;
				hullCtx.stroke();
			}

			// Source badges, on the TOP canvas, snug on each node's edge.
			g.forEachNode((id, attrs) => {
				const node = (attrs as { _node?: GraphNode })._node;
				if (!node?.source) return;
				const glyph = sourceGlyphFor(node.source);
				if (!glyph) return;
				const screenR = renderer.scaleSize(attrs.size ?? NODE_SIZE_BASE);
				if (screenR < 6) return;
				const vp = renderer.graphToViewport({ x: attrs.x, y: attrs.y });
				// Badge sits overlapping the node's lower-right rim (a small
				// corner emblem), not floating off it. Capped so it stays a
				// mark, not a second node, when zoomed in.
				const badgeR = Math.min(Math.max(screenR * 0.42, 4), 8);
				const offset = screenR * 0.62;
				drawSourceBadge(badgeCtx, vp.x + offset, vp.y + offset, badgeR, glyph);
			});
		};

		renderer.on("afterRender", draw);
		draw();

		return () => {
			renderer.off("afterRender", draw);
			if (hullCanvas.parentNode) hullCanvas.parentNode.removeChild(hullCanvas);
			if (overlay.parentNode) overlay.parentNode.removeChild(overlay);
			renderer.kill();
			sigmaRef.current = null;
		};
	}, [graphQ.data]);

	// Honor `?focus=<node_id>` on load. Ask-palette source / entity
	// card clicks navigate here; this effect pans the camera + selects
	// once the graph payload + sigma instance are both ready. After
	// focusing, the param is stripped so a refresh doesn't keep
	// re-zooming.
	const focusParam = searchParams.get("focus");
	useEffect(() => {
		if (!focusParam) return;
		// A two-frame RAF gap lets sigma finish its first paint on the
		// new graph before we animate the camera; without it the focus
		// fires against an un-positioned graph and the camera lands at
		// the origin.
		let raf1 = 0;
		let raf2 = 0;
		raf1 = requestAnimationFrame(() => {
			raf2 = requestAnimationFrame(() => {
				const nodes = graphQ.data?.nodes;
				if (nodes) navigateToNode(focusParam, nodes);
				const next = new URLSearchParams(searchParams);
				next.delete("focus");
				setSearchParams(next, { replace: true });
			});
		});
		return () => {
			cancelAnimationFrame(raf1);
			cancelAnimationFrame(raf2);
		};
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [focusParam, graphQ.data]);

	return (
		<div className="relative h-full">
			{/* Stats strip floats on top-left of the canvas. */}
			<GlassPanel
				variant="hi"
				className="absolute top-3 left-3 z-10 px-4 py-2 flex items-center gap-4 text-[12px]"
			>
				<StatItem label="nodes" value={fmt(statsQ.data?.node_count)} />
				<Divider />
				<StatItem label="edges" value={fmt(statsQ.data?.edge_count)} />
				<Divider />
				<StatItem label="vectors" value={fmt(statsQ.data?.vector_count)} />
				<Divider />
				<StatItem label="tier" value={statsQ.data?.vector_tier ?? "—"} />
			</GlassPanel>
			{presentSources.length > 0 && (
				<GlassPanel
					variant="hi"
					className="absolute top-16 left-3 z-10 px-3 py-2 flex flex-wrap items-center gap-2 text-[11px]"
				>
					{presentSources.map((source) => {
						const glyph = SOURCE_GLYPH[source];
						if (!glyph) return null;
						return (
							<span key={source} className="inline-flex items-center gap-1.5 text-[var(--color-text-muted)]">
								<SourceBrandIcon source={source} className="w-3 h-3" />
								{glyph.label}
							</span>
						);
					})}
				</GlassPanel>
			)}

			{/* Sigma canvas fills the page. */}
			<div ref={containerRef} className="sigma-container rounded-[var(--radius-panel)] overflow-hidden border border-[var(--color-border)]" />

			{/* Detail panel slides in from the right when a node is selected. */}
			{selected && graphQ.data && (
				<DetailPanel
					selected={selected}
					data={graphQ.data}
					onClose={() => setSelected(null)}
					onNavigate={(id) => navigateToNode(id, graphQ.data!.nodes)}
				/>
			)}

			{/* Zoom controls: bottom-left so they don't fight the detail
			    panel on the right. Always rendered but no-op until sigma
			    finishes mounting. */}
			<ZoomControls sigmaRef={sigmaRef} />

			{/* Loading hint (only when truly empty). */}
			{graphQ.isLoading && (
				<div className="absolute inset-0 flex items-center justify-center text-[12px] text-[var(--color-text-dim)] pointer-events-none">
					loading graph…
				</div>
			)}
			{graphQ.isSuccess && graphQ.data.nodes.length === 0 && (
				<div className="absolute inset-0 flex items-center justify-center text-[13px] text-[var(--color-text-muted)] pointer-events-none">
					<div className="text-center space-y-1">
						<div>No memory yet.</div>
						<div className="text-[var(--color-text-dim)] mono">v3 ingest-file 'docs/**/*.md'</div>
					</div>
				</div>
			)}
		</div>
	);
}

interface ZoomControlsProps {
	sigmaRef: React.RefObject<Sigma | null>;
}

/**
 * Three-button glass panel: zoom in, zoom out, fit-to-view. Uses
 * sigma's camera animation primitives so the transitions match the
 * mouse-wheel feel.
 */
function ZoomControls({ sigmaRef }: ZoomControlsProps) {
	const zoom = (factor: number) => {
		const r = sigmaRef.current;
		if (!r) return;
		const cam = r.getCamera();
		const state = cam.getState();
		cam.animate(
			{ ...state, ratio: state.ratio / factor },
			{ duration: 220 },
		);
	};
	const fit = () => {
		const r = sigmaRef.current;
		if (!r) return;
		r.getCamera().animatedReset({ duration: 320 });
	};

	return (
		<GlassPanel
			variant="hi"
			className="absolute bottom-3 left-3 z-10 flex items-center gap-0.5 px-1 py-1"
		>
			<button
				type="button"
				onClick={() => zoom(1.5)}
				aria-label="Zoom in"
				title="Zoom in"
				className="w-7 h-7 flex items-center justify-center rounded-md text-[var(--color-text-muted)] hover:text-[var(--color-text)] hover:bg-white/[0.06] transition-colors"
			>
				<Plus className="w-3.5 h-3.5" />
			</button>
			<button
				type="button"
				onClick={() => zoom(1 / 1.5)}
				aria-label="Zoom out"
				title="Zoom out"
				className="w-7 h-7 flex items-center justify-center rounded-md text-[var(--color-text-muted)] hover:text-[var(--color-text)] hover:bg-white/[0.06] transition-colors"
			>
				<Minus className="w-3.5 h-3.5" />
			</button>
			<div className="w-px h-4 bg-[var(--color-border)] mx-0.5" />
			<button
				type="button"
				onClick={fit}
				aria-label="Fit to view"
				title="Fit to view"
				className="w-7 h-7 flex items-center justify-center rounded-md text-[var(--color-text-muted)] hover:text-[var(--color-text)] hover:bg-white/[0.06] transition-colors"
			>
				<Maximize2 className="w-3.5 h-3.5" />
			</button>
		</GlassPanel>
	);
}

interface DetailPanelProps {
	selected: GraphNode;
	data: GraphResponse;
	onClose: () => void;
	onNavigate: (nodeId: string) => void;
}

function DetailPanel({ selected, data, onClose, onNavigate }: DetailPanelProps) {
	const nodesById = useMemo(
		() => new Map(data.nodes.map((n) => [n.id, n])),
		[data.nodes],
	);
	const outgoing = useMemo(
		() => data.edges.filter((e) => e.from === selected.id),
		[data.edges, selected.id],
	);
	const incoming = useMemo(
		() => data.edges.filter((e) => e.to === selected.id),
		[data.edges, selected.id],
	);

	return (
		<GlassPanel
			variant="hi"
			className="absolute top-3 right-3 bottom-3 z-10 w-[360px] flex flex-col"
		>
			<div className="shrink-0 flex items-center justify-between px-4 py-3 border-b border-[var(--color-border)]">
				<div className="min-w-0">
					<div className="text-[10px] uppercase tracking-wider text-[var(--color-text-dim)]">
						{selected.type}
					</div>
					<div className="text-[13px] truncate mono">{selected.external_id}</div>
				</div>
				<button
					type="button"
					onClick={onClose}
					className="text-[var(--color-text-dim)] hover:text-[var(--color-text)] transition-colors"
				>
					<X className="w-4 h-4" />
				</button>
			</div>
			<div className="flex-1 overflow-y-auto p-4 space-y-4 text-[12px]">
				<KV label="source" value={selected.source} mono />
				<KV label="id" value={selected.id} mono />

				{outgoing.length > 0 && (
					<EdgeList
						label={`outgoing · ${outgoing.length}`}
						edges={outgoing}
						getOther={(e) => e.to}
						icon={<ArrowUpRight className="w-3 h-3 shrink-0 text-[var(--color-text-dim)]" />}
						nodesById={nodesById}
						onNavigate={onNavigate}
					/>
				)}
				{incoming.length > 0 && (
					<EdgeList
						label={`incoming · ${incoming.length}`}
						edges={incoming}
						getOther={(e) => e.from}
						icon={<ArrowDownRight className="w-3 h-3 shrink-0 text-[var(--color-text-dim)]" />}
						nodesById={nodesById}
						onNavigate={onNavigate}
					/>
				)}

				<div>
					<div className="text-[10px] uppercase tracking-wider text-[var(--color-text-dim)] mb-1">
						payload
					</div>
					<pre className="mono text-[11px] text-[var(--color-text-muted)] whitespace-pre-wrap break-words bg-black/30 rounded-md p-2 border border-[var(--color-border)] max-h-[280px] overflow-y-auto">
						{prettyJson(selected.payload)}
					</pre>
				</div>
			</div>
		</GlassPanel>
	);
}

interface EdgeListProps {
	label: string;
	edges: GraphEdge[];
	getOther: (e: GraphEdge) => string;
	icon: React.ReactNode;
	nodesById: Map<string, GraphNode>;
	onNavigate: (nodeId: string) => void;
}

function EdgeList({ label, edges, getOther, icon, nodesById, onNavigate }: EdgeListProps) {
	return (
		<div>
			<div className="text-[10px] uppercase tracking-wider text-[var(--color-text-dim)] mb-1.5">
				{label}
			</div>
			<div className="space-y-0.5">
				{edges.map((e) => {
					const otherId = getOther(e);
					const other = nodesById.get(otherId);
					return (
						<button
							key={e.id}
							type="button"
							onClick={() => onNavigate(otherId)}
							disabled={!other}
							className="w-full flex items-center gap-2 px-2 py-1.5 rounded-md text-left hover:bg-white/5 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
						>
							{icon}
							<span className="mono text-[10px] text-[var(--color-text-dim)] shrink-0 min-w-[64px]">
								{e.relation}
							</span>
							<span className="mono text-[11px] truncate flex-1">
								{other?.external_id ?? `${otherId.slice(0, 8)}… (out of view)`}
							</span>
						</button>
					);
				})}
			</div>
		</div>
	);
}

function StatItem({ label, value }: { label: string; value: string | number | undefined }) {
	return (
		<div className="flex items-baseline gap-1.5">
			<span className="text-[var(--color-text-dim)] uppercase tracking-wider text-[10px]">
				{label}
			</span>
			<span className="mono text-[12px]">{value ?? "—"}</span>
		</div>
	);
}

function Divider() {
	return <span className="w-px h-3 bg-[var(--color-border)]" />;
}

function KV({ label, value, mono }: { label: string; value: string; mono?: boolean }) {
	return (
		<div>
			<div className="text-[10px] uppercase tracking-wider text-[var(--color-text-dim)] mb-1">
				{label}
			</div>
			<div className={`text-[12px] truncate ${mono ? "mono" : ""}`}>{value}</div>
		</div>
	);
}

function fmt(n: number | undefined): string {
	if (n === undefined) return "—";
	return n.toLocaleString();
}

function shortLabel(n: GraphNode): string {
	const ext = n.external_id;
	if (ext.length <= 24) return ext;
	return `…${ext.slice(-23)}`;
}

function prettyJson(v: unknown): string {
	try {
		return JSON.stringify(v, null, 2);
	} catch {
		return String(v);
	}
}
